import { useState, useCallback, useEffect } from "react"
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js"
import type { StripePaymentElementOptions } from "@stripe/stripe-js"
import { Loader2, CheckCircle2 } from "lucide-react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { FunctionsHttpError } from "@supabase/supabase-js"
import { trackEvent } from "@/lib/posthog"
import { stripePromise } from "@/lib/stripe"
import { supabase } from "@/lib/supabase"

const PRESET_AMOUNTS = [25, 50, 100]
const MIN_AMOUNT_CENTS = 100
const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

function attributionFromBrowser(): { campaign_id?: string; fundraiser_id?: string } {
  if (typeof window === "undefined") return {}
  const q = new URLSearchParams(window.location.search)
  const cid = q.get("campaign_id")
  const fid = q.get("fundraiser_id")
  const out: { campaign_id?: string; fundraiser_id?: string } = {}
  if (cid && UUID_REGEX.test(cid)) out.campaign_id = cid
  if (fid && UUID_REGEX.test(fid)) out.fundraiser_id = fid
  return out
}

export interface DonationModalProps {
  isOpen: boolean
  onClose: () => void
  orgId: string
  /** Optional: will be fetched from organizations if not provided (e.g. anonymous public donations) */
  orgName?: string
  /** Alias for orgId when passing only organization id */
  organizationId?: string
  /** Org's brand color — used to style buttons and Stripe appearance */
  primaryColor?: string
  /** When set (valid UUID), overrides `?campaign_id` from the page URL for PaymentIntent metadata. */
  campaignId?: string | null
  /** When set (valid UUID), overrides `?fundraiser_id` from the page URL. */
  fundraiserId?: string | null
  /** NOS-162: preselect an amount (from the embedded donation form block). */
  initialAmountCents?: number | null
}

function PaymentForm({
  amountCents,
  donorName,
  donorEmail,
  onSuccess,
  onError,
  primaryColor,
}: {
  amountCents: number
  donorName: string
  donorEmail: string
  onSuccess: () => void
  onError: (message: string) => void
  primaryColor?: string
}) {
  const stripe = useStripe()
  const elements = useElements()
  const [isProcessing, setIsProcessing] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!stripe || !elements) return

    setIsProcessing(true)
    try {
      const { error } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: window.location.href,
          receipt_email: donorEmail,
          payment_method_data: {
            billing_details: {
              name: donorName,
              email: donorEmail,
            },
          },
        },
        redirect: "if_required",
      })

      if (error) {
        onError(error.message ?? "Payment failed")
      } else {
        onSuccess()
      }
    } catch (err) {
      onError(err instanceof Error ? err.message : "An unexpected error occurred")
    } finally {
      setIsProcessing(false)
    }
  }

  const amountDollars = (amountCents / 100).toFixed(2)

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="rounded-lg border bg-muted/30 p-4">
        <PaymentElement
          options={{
            layout: "tabs",
          } as StripePaymentElementOptions}
        />
      </div>
      <Button
        type="submit"
        className="w-full"
        size="lg"
        disabled={!stripe || !elements || isProcessing}
        style={primaryColor ? { backgroundColor: primaryColor, borderColor: primaryColor } : undefined}
      >
        {isProcessing ? (
          <>
            <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
            Processing...
          </>
        ) : (
          `Donate $${amountDollars}`
        )}
      </Button>
    </form>
  )
}

export function DonationModal({
  isOpen,
  onClose,
  orgId: orgIdProp,
  orgName: orgNameProp,
  organizationId,
  primaryColor,
  campaignId: campaignIdProp,
  fundraiserId: fundraiserIdProp,
  initialAmountCents,
}: DonationModalProps) {
  const orgId = organizationId ?? orgIdProp
  const [fetchedOrgName, setFetchedOrgName] = useState<string | null>(null)
  const orgName = orgNameProp ?? fetchedOrgName ?? "this organization"

  const [amountCents, setAmountCents] = useState<number | null>(initialAmountCents ?? null)
  const [customAmount, setCustomAmount] = useState("")
  const [donorName, setDonorName] = useState("")
  const [donorEmail, setDonorEmail] = useState("")
  const [clientSecret, setClientSecret] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [successAmount, setSuccessAmount] = useState<string>("")

  useEffect(() => {
    if (!orgId || orgNameProp) return
    let cancelled = false
    supabase
      .from("organizations")
      .select("name")
      .eq("id", orgId)
      .single()
      .then(({ data }) => {
        if (!cancelled && data?.name) setFetchedOrgName(data.name)
      })
    return () => {
      cancelled = true
    }
  }, [orgId, orgNameProp])

  const isCustomAmount = amountCents === null
  const resolvedAmountCents = isCustomAmount
    ? Math.round(parseFloat(customAmount || "0") * 100)
    : amountCents

  const canProceed =
    resolvedAmountCents >= MIN_AMOUNT_CENTS &&
    donorName.trim().length > 0 &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(donorEmail.trim())

  const resetForm = useCallback(() => {
    setAmountCents(null)
    setCustomAmount("")
    setDonorName("")
    setDonorEmail("")
    setClientSecret(null)
    setError(null)
    setSuccess(false)
    setSuccessAmount("")
  }, [])

  const handleClose = useCallback(() => {
    onClose()
    if (success) {
      resetForm()
    }
  }, [onClose, success, resetForm])

  const handleInitialSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!canProceed) return

    setIsLoading(true)
    setError(null)

    const fromUrl = attributionFromBrowser()
    const campaign_id =
      typeof campaignIdProp === "string" && UUID_REGEX.test(campaignIdProp)
        ? campaignIdProp
        : fromUrl.campaign_id
    const fundraiser_id =
      typeof fundraiserIdProp === "string" && UUID_REGEX.test(fundraiserIdProp)
        ? fundraiserIdProp
        : fromUrl.fundraiser_id

    const body = {
      org_id: orgId,
      amount_cents: resolvedAmountCents,
      donor_email: donorEmail.trim(),
      donor_name: donorName.trim(),
      ...(campaign_id ? { campaign_id } : {}),
      ...(fundraiser_id ? { fundraiser_id } : {}),
    }
    try {
      const { data, error: fnError } = await supabase.functions.invoke(
        "create-payment-intent",
        { body }
      )

      if (fnError) {
        let message = fnError.message ?? "Failed to create payment"
        if (fnError instanceof FunctionsHttpError && fnError.context) {
          try {
            const body = await (fnError.context as Response).json() as { error?: string }
            if (typeof body?.error === "string") message = body.error
          } catch {
            // keep message from fnError.message
          }
        }
        trackEvent("donation_failed", {
          org_id: orgId,
          amount_cents: resolvedAmountCents,
          error: message,
        })
        setError(message)
        return
      }

      const secret = data?.client_secret
      if (!secret || typeof secret !== "string") {
        trackEvent("donation_failed", {
          org_id: orgId,
          amount_cents: resolvedAmountCents,
          error: data?.error ?? "Invalid response from server",
        })
        setError(data?.error ?? "Invalid response from server")
        return
      }

      trackEvent("donation_started", {
        org_id: orgId,
        amount_cents: resolvedAmountCents,
      })
      setClientSecret(secret)
    } catch (err) {
      trackEvent("donation_failed", {
        org_id: orgId,
        amount_cents: resolvedAmountCents,
        error: err instanceof Error ? err.message : "An unexpected error occurred",
      })
      setError(err instanceof Error ? err.message : "An unexpected error occurred")
    } finally {
      setIsLoading(false)
    }
  }

  const handlePaymentSuccess = useCallback(() => {
    trackEvent("donation_completed", {
      org_id: orgId,
      amount_cents: resolvedAmountCents,
      donation_amount: resolvedAmountCents / 100,
    })
    setSuccess(true)
    setSuccessAmount((resolvedAmountCents / 100).toFixed(2))
  }, [orgId, resolvedAmountCents])

  useEffect(() => {
    if (!isOpen) resetForm()
  }, [isOpen, resetForm])

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        onClose()
      }, 3000)
      return () => clearTimeout(timer)
    }
  }, [success, onClose])

  const handlePaymentError = useCallback((message: string) => {
    trackEvent("donation_failed", {
      org_id: orgId,
      amount_cents: resolvedAmountCents,
      error: message,
    })
    setError(message)
  }, [orgId, resolvedAmountCents])

  // Success state: celebratory message, auto-close after 3 seconds
  if (success) {
    return (
      <Dialog open={isOpen} onOpenChange={() => handleClose()}>
        <DialogContent
          className="max-h-[90vh] overflow-y-auto sm:max-w-md"
          onPointerDownOutside={(e) => e.preventDefault()}
        >
          <div className="flex flex-col items-center py-6 text-center">
            <CheckCircle2
              className="h-16 w-16 text-green-600"
              aria-hidden
            />
            <DialogTitle className="mt-4 text-xl">
              Thank you!
            </DialogTitle>
            <DialogDescription className="mt-2 text-base">
              Your donation of ${successAmount} to {orgName} was successful.
            </DialogDescription>
          </div>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent
        className="max-h-[90vh] overflow-y-auto sm:max-w-md"
        aria-describedby={undefined}
      >
        <DialogHeader>
          <DialogTitle>Donate to {orgName}</DialogTitle>
          <DialogDescription id="donation-modal-desc">
            Support {orgName} with a one-time donation. All fields are required.
          </DialogDescription>
        </DialogHeader>

        {!clientSecret ? (
          <form onSubmit={handleInitialSubmit} className="space-y-6">
            <div className="space-y-3">
              <Label htmlFor="amount-presets">Amount</Label>
              <div
                id="amount-presets"
                className="flex flex-wrap gap-2"
                role="group"
                aria-label="Select donation amount"
              >
                {PRESET_AMOUNTS.map((dollars) => (
                  <Button
                    key={dollars}
                    type="button"
                    variant={amountCents === dollars * 100 ? "default" : "outline"}
                    size="sm"
                    onClick={() => {
                      setAmountCents(dollars * 100)
                      setCustomAmount("")
                    }}
                    style={primaryColor && amountCents === dollars * 100 ? { backgroundColor: primaryColor, borderColor: primaryColor } : undefined}
                  >
                    ${dollars}
                  </Button>
                ))}
                <Button
                  type="button"
                  variant={isCustomAmount ? "default" : "outline"}
                  size="sm"
                  onClick={() => setAmountCents(null)}
                  style={primaryColor && isCustomAmount ? { backgroundColor: primaryColor, borderColor: primaryColor } : undefined}
                >
                  Custom
                </Button>
              </div>
              {isCustomAmount && (
                <div className="relative mt-2">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                    $
                  </span>
                  <Input
                    type="number"
                    min="1"
                    step="0.01"
                    placeholder="Enter amount"
                    className="pl-7"
                    value={customAmount}
                    onChange={(e) => setCustomAmount(e.target.value)}
                    aria-label="Custom donation amount in dollars"
                  />
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="donor-name">Name</Label>
              <Input
                id="donor-name"
                type="text"
                placeholder="Your name"
                value={donorName}
                onChange={(e) => setDonorName(e.target.value)}
                required
                aria-required="true"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="donor-email">Email</Label>
              <Input
                id="donor-email"
                type="email"
                placeholder="you@example.com"
                value={donorEmail}
                onChange={(e) => setDonorEmail(e.target.value)}
                required
                aria-required="true"
              />
            </div>

            {error && (
              <p className="text-sm text-destructive" role="alert">
                {error}
              </p>
            )}

            <Button
              type="submit"
              className="w-full"
              size="lg"
              disabled={!canProceed || isLoading}
              style={primaryColor ? { backgroundColor: primaryColor, borderColor: primaryColor } : undefined}
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
                  Preparing...
                </>
              ) : (
                `Donate $${(resolvedAmountCents / 100).toFixed(2)}`
              )}
            </Button>
          </form>
        ) : (
          <Elements
            stripe={stripePromise}
            options={{
              clientSecret,
              appearance: {
                theme: "stripe" as const,
                ...(primaryColor ? { variables: { colorPrimary: primaryColor } } : {}),
              },
            }}
          >
            <PaymentForm
              amountCents={resolvedAmountCents}
              donorName={donorName.trim()}
              donorEmail={donorEmail.trim()}
              onSuccess={handlePaymentSuccess}
              onError={handlePaymentError}
              primaryColor={primaryColor}
            />
          </Elements>
        )}

        {clientSecret && error && (
          <p className="text-sm text-destructive" role="alert">
            {error}
          </p>
        )}
      </DialogContent>
    </Dialog>
  )
}
